<template>
  <div class="w-100 grid-container">
    <configuration-section-header
      class="my-0"
      :section-icon="'fal fa-list-ul'"
      :section-title="$tc('configuration.sections.departmentType', 2)"
      @changedPageSize="changedPageSize($event)"
      @changedMode="changedMode($event)"
    >
      <b-button
        v-tooltip="$t('configuration.actions.activate')"
        :disabled="!canActivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(true, selectedRows)"
      >
        <i
          class="far fa-check-circle"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-tooltip="$t('configuration.actions.deactivate')"
        :disabled="!canDeactivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(false, selectedRows)"
      >
        <i
          class="far fa-times-circle"
          aria-hidden="true"
        />
      </b-button>
      <div
        style="width:1px; height:18px; background: #d0d0d0"
        class="mx-3"
      />
      <b-button
        v-shortkey="['alt', 'del']"
        v-tooltip="$t('configuration.actions.delete')"
        :disabled="selectedRows.length < 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="deleteDepartmentTypes(selectedRows)"
        @shortkey="deleteDepartmentTypes(selectedRows)"
      >
        <i
          class="far fa-trash-alt"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['alt', 'e']"
        v-tooltip="$t('configuration.actions.update')"
        :disabled="selectedRows.length !== 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showDepartmentTypesModal($t('configuration.actions.update'), selectedRows[0])"
        @shortkey="showDepartmentTypesModal($t('configuration.actions.update'), selectedRows[0])"
      >
        <i
          class="far fa-edit"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['ctrl', 'enter']"
        v-tooltip="$t('configuration.actions.add')"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showDepartmentTypesModal($t('configuration.actions.add'))"
        @shortkey="showDepartmentTypesModal($t('configuration.actions.add'))"
      >
        <i
          class="far fa-plus"
          aria-hidden="true"
        />
      </b-button>
    </configuration-section-header>
    <div class="flex-grow-1">
      <vt-grid
        class="configuration-grid"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-code="gridUiComponent"
      />
    </div>
    <modal-wrapper ref="departmentTypesModalWrapper">
      <departments-types-configuration-modal
        :action-title="actionTitle"
        @update="patchDepartmentTypes($event)"
        @create="createDepartmentTypes($event)"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import DepartmentsTypesConfigurationModal from '@/modules/configurationModule/detail/centersAndServices/departmentsTypes/DepartmentsTypesConfigurationModal'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import ConfigurationDepartmentTypesGridColumns
    from '@/modules/configurationModule/detail/centersAndServices/departmentsTypes/ConfigurationDepartmentTypesGridColumns'
import ConfigurationDepartmentTypeServices
    from '@/api/resources/configuration/centersAndAreas/departmentType/ConfigurationDepartmentTypeServices'
import { PaginatorDatasource } from '@/shared/resources/datasources/paginator/paginator-datasource'

export default {
    name: 'DepartmentsTypesConfigurationGrid',
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        DepartmentsTypesConfigurationModal
    },
    data() {
        return {
            gridUiComponent: 'uicomponent.configuration.global.centersAndServices.departmentTypes',
            actionTitle: null,
            selectedRows: [],
            columnDefs: ConfigurationDepartmentTypesGridColumns(),
            mode: {
                size: 25
            }
        }
    },
    computed: {
        msgSelectedRows() {
            let msg = `${this.selectedRows.length} ${this.$tc('global.selectedElements', this.selectedRows.length)}`
            if (this.selectedRows.length === 0) msg = ''
            return msg
        },
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                getContextMenuItems: this.getContextMenuItems,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                getRowHeight: () => this.rowHeight
            }
        },
        hasActiveDepartmentType() {
            return !!this.selectedRows.find(departmentType => departmentType.active)
        },
        hasDeactivatedDepartmentType() {
            return !!this.selectedRows.find(departmentType => !departmentType.active)
        },
        hasAllActivated() {
            return (this.selectedRows.length === this.selectedRows.filter(departmentType => departmentType.active).length)
        },
        hasAllDeactivated() {
            return (this.selectedRows.length === this.selectedRows.filter(departmentType => !departmentType.active).length)
        },
        canDeactivateBatch() {
            return this.selectedRows.length && this.hasActiveDepartmentType && !this.hasAllDeactivated
        },
        canActivateBatch() {
            return this.hasDeactivatedDepartmentType && !this.hasAllActivated
        },
        rowHeight() {
            return this.mode?.size
        }
    },
    methods: {
        async activateDeactivateSelection(option, selectionBatch) {
            const departmentTypesToPatch = selectionBatch.filter(departmentType => departmentType.active !== option).map(departmentType => {
                return {
                    active: option,
                    id: departmentType.id
                }
            })
            if (departmentTypesToPatch.length) {
                this.updateLoading(true)
                const [err] = await this.toCall(ConfigurationDepartmentTypeServices.patchDepartmentTypesBatch(departmentTypesToPatch))
                this.updateLoading(false)
                if (err) {
                    this.showErrorModalGlobalFromErrorResponse(err)
                    return
                }
                if (selectionBatch.length > 1) this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 2, { elements: departmentTypesToPatch.length }))
                else this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 1))
                this.reloadData()
            }
        },
        async patchDepartmentTypes(...departmentTypes) {
            this.updateLoading(true)
            const [err, data] = await this.toCall(ConfigurationDepartmentTypeServices.patchDepartmentTypesBatch(departmentTypes))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            } else if (data.length) {
                this.handleErrors(data)
                return
            }
            this.$refs.departmentTypesModalWrapper.hide()
            this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 1))
            this.reloadData()
        },
        async createDepartmentTypes(departmentType) {
            this.updateLoading(true)
            const [err] = await this.toCall(ConfigurationDepartmentTypeServices.createDepartmentType(departmentType))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            }
            this.$refs.departmentTypesModalWrapper.hide()
            this.showToastSuccessGlobal(this.$t('configuration.toastMessages.created'))
            this.reloadData()
        },
        async deleteDepartmentTypesBatch(departmentTypes) {
            const departmentTypesIds = departmentTypes.map(area => area.id)
            this.updateLoading(true)
            const [err, response] = await this.toCall(ConfigurationDepartmentTypeServices.deleteDepartmentTypesBatch(departmentTypesIds))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            } else if (response.length) {
                this.handleErrors(response)
                return
            }
            if (departmentTypesIds.length > 1) this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 2, { elements: departmentTypesIds.length }))
            else this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 1))
            this.reloadData()
        },
        async deleteDepartmentTypes(departmentTypes) {
            if (departmentTypes.length) {
                const promise = await this.showConfirmationModalGlobal(`${this.$t('configuration.grid.deleteConfirmation')} ${this.selectedRows.length} ${this.$tc('configuration.grid.element', this.selectedRows.length)}, <br/> ${this.$t('configuration.grid.deleteQuestion')}`, this.$t('configuration.actions.delete'))
                if (promise) {
                    await this.deleteDepartmentTypesBatch(departmentTypes)
                }
            }
        },
        reloadData() {
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(new PaginatorDatasource(ConfigurationDepartmentTypeServices))
        },
        getContextMenuItems(params) {
            if (!params?.node) {
                return []
            } else {
                return [
                    {
                        name: this.$t('configuration.actions.update'),
                        cssClasses: this.selectedRows.length > 1 ? ['not-show'] : '',
                        action: () => this.showDepartmentTypesModal(this.$t('configuration.actions.update'), params?.node.data)
                    },
                    {
                        name: this.$t('configuration.actions.delete'),
                        cssClasses: this.selectedRows.length < 1 ? ['not-show'] : '',
                        action: () => this.deleteDepartmentTypes(this.selectedRows)
                    },
                    {
                        name: params?.node.data.active ? this.$t('configuration.actions.deactivate') : this.$t('configuration.actions.activate'),
                        cssClasses: this.selectedRows.length ? ['not-show'] : '',
                        action: () => this.activateDeactivateSelection(!params.node?.data.active, [params?.node.data])
                    },
                    {
                        name: this.$t('configuration.actions.activate'),
                        cssClasses: this.canActivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(true, this.selectedRows)
                    },
                    {
                        name: this.$t('configuration.actions.deactivate'),
                        cssClasses: this.canDeactivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(false, this.selectedRows)
                    }
                ]
            }
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        onGridReady({ api }) {
            this.gridApi = api
            api.setServerSideDatasource(new PaginatorDatasource(ConfigurationDepartmentTypeServices))
        },
        showDepartmentTypesModal(title, selectedDepartmentType) {
            this.actionTitle = title
            this.$refs.departmentTypesModalWrapper.show([selectedDepartmentType])
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            this.reloadData()
        }
    }
}
</script>
